/**
 * Better-Auth client (React) — magic link flow.
 *
 * baseURL = Workers API base (VITE_API_URL). Better-Auth handler is mounted at /api/auth/*
 * on Workers. The client makes credentials: 'include' fetches.
 *
 * Type erasure note (Sprint 2.1 P5) :
 *   Better-Auth v1.6.9 + zod peer mismatch → TS2742 "not portable" on inferred types.
 *   We export structurally-typed wrappers around `authClient` (callers should prefer
 *   `fetchSession`, `requestMagicLink`, `signOut` over the raw `any` client).
 *
 * Backend (Hono Worker apps/workers) :
 *   - /api/auth/*            : Better-Auth handlers (magic-link, get-session, sign-out)
 *   - /api/auth/validate-signup : Edge gating <18 + atomic insert user + consent_log
 */
import { createAuthClient } from 'better-auth/react';
import { magicLinkClient } from 'better-auth/client/plugins';
import { API_BASE_URL } from './api-client';
// Better-Auth handler is mounted at <baseURL>/api/auth/* on the Workers
// (createAuthClient appends /api/auth automatically when given the API root).
// If baseURL = 'http://localhost:8787' the client will POST to
// 'http://localhost:8787/api/auth/sign-in/magic-link' which matches our Hono mount.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const authClient = createAuthClient({
    baseURL: API_BASE_URL,
    plugins: [magicLinkClient()],
});
/** Returns the current session or null if unauthenticated. */
export async function fetchSession() {
    try {
        const res = await fetch(`${API_BASE_URL}/api/auth/get-session`, {
            method: 'GET',
            credentials: 'include',
            headers: { Accept: 'application/json' },
        });
        if (!res.ok)
            return null;
        const data = (await res.json());
        if (!data || !data.session || !data.user)
            return null;
        return { user: data.user, session: data.session };
    }
    catch {
        return null;
    }
}
/** Re-sends magic link to an existing user (idempotent). */
export async function requestMagicLink(email, callbackURL) {
    const url = `${API_BASE_URL}/api/auth/sign-in/magic-link`;
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
            email,
            callbackURL: callbackURL ?? `${window.location.origin}/auth/verify`,
        }),
    });
    if (!res.ok) {
        throw new Error(`request_magic_link_failed_${res.status}`);
    }
}
export async function signOut() {
    try {
        await fetch(`${API_BASE_URL}/api/auth/sign-out`, {
            method: 'POST',
            credentials: 'include',
        });
    }
    catch {
        /* ignore network errors — caller clears local state anyway */
    }
}
//# sourceMappingURL=auth.js.map